import type { CommunityMemberCharge } from "@circle-react/types/CommunityMemberCharge";
import type { CommunityMemberSubscription } from "@circle-react/types/CommunityMemberSubscription";
import type { PaywallCoupon } from "@circle-react/types/PaywallCoupon";
import type { PaywallPrice } from "@circle-react/types/PaywallPrice";

import type { CommunityMemberBillingInfo } from "@/react/types/CommunityMemberBillingInfo";
import type { CurrentCommunityMember } from "@/react/types/CurrentCommunityMember";
import type { CurrentUser } from "@/react/types/CurrentUser";
import type {
  CouponApplications,
  Paywall,
  PaywallCurrency,
} from "@/react/types/Paywall";
import type { PaywallOffer } from "@/react/types/PaywallOffer";

export interface PaymentValidationParams {
  invoiceId?: string;
  paymentIntentId?: string;
  setupIntentId?: string;
}

export interface CheckoutSessionCommunityMember extends CurrentCommunityMember {
  billing_info?: CommunityMemberBillingInfo;
}

export interface CheckoutSessionPaywall extends Paywall {
  member_payment_processor?: {
    is_member_with_saved_payment_methods: boolean;
    session_client_secret: string | null;
  };
  policies: {
    are_coupons_enabled: boolean;
    is_tax_id_mandatory: boolean;
    has_already_purchased: boolean;
    can_update_all_members_access: boolean;
    can_change_currency: boolean;
    requires_currency: boolean;
  };
  title: string;
}

export const checkoutSessionStatuses = {
  pending: "pending",
  completed: "completed",
  abandoned: "abandoned",
  skipped: "skipped",
} as const;

export type CheckoutSessionStatus =
  (typeof checkoutSessionStatuses)[keyof typeof checkoutSessionStatuses];

export const checkoutSessionStepStatuses = {
  pending: "pending",
  completed: "completed",
  abandoned: "abandoned",
  offered: "offered",
  processing: "processing",
  declined: "declined",
  failed: "failed",
  skipped: "skipped",
} as const;

export type CheckoutSessionStepStatus =
  (typeof checkoutSessionStepStatuses)[keyof typeof checkoutSessionStepStatuses];

export interface CheckoutSessionStepOffer {
  id: number;
  main: boolean;
  paywall: CheckoutSessionPaywall;
  paywall_offer: PaywallOffer | null;
}

export type CheckoutSessionStepPaywallPrice = Pick<
  PaywallPrice,
  | "id"
  | "title"
  | "amount"
  | "installments_count"
  | "interval"
  | "interval_count"
  | "interval_type"
  | "preferred"
  | "trial_requires_upfront_payment"
  | "upfront_payment_amount"
  | "price_type"
  | "scenario"
  | "trial_days"
> & {
  amount_decimal: number;
  currency: Pick<
    PaywallCurrency,
    "id" | "code" | "symbol" | "smallest_unit" | "direction"
  >;
  redirect_event_url?: string;
  upfront_payment_amount_decimal?: number;
};

export interface CheckoutSessionStep {
  community_member_charge: Pick<
    CommunityMemberCharge,
    | "id"
    | "amount"
    | "payment_method_type"
    | "tax_amount"
    | "invoice_processor_id"
    | "processor_id"
    | "status"
    | "created_at"
  > & {
    // amount_decimal: charge amount in major currency units (via
    // Currency#amount). Absent for subscription-linked charges (serializer SKIP).
    amount_decimal?: number;
    // The data below wont't be available if the price is a recurring price
    // Check `community_member_subscription` to see the data
    paywall_price?: CheckoutSessionStepPaywallPrice;
    coupon_applications?: CouponApplications;
    paywall_coupon?: Pick<PaywallCoupon, "id" | "code">;
  };
  community_member_subscription: Pick<
    CommunityMemberSubscription,
    | "id"
    | "ends_at"
    | "trial_ends_at"
    | "renews_at"
    | "status"
    | "created_at"
    | "processor_id"
  > & {
    paywall_price?: CheckoutSessionStepPaywallPrice;
    paywall_id?: number;
    paywall_display_name?: string;
    coupon_applications?: CouponApplications;
    paywall_coupon?: Pick<PaywallCoupon, "id" | "code">;
  };
  conversion_tracking_js_code?: string;
  id: number;
  offers: CheckoutSessionStepOffer[];
  sequence: number;
  status: CheckoutSessionStepStatus;
}

export interface CheckoutSessionCommunity {
  icon_url: string | null;
  id: number;
  logo_url: string | null;
  name: string;
  payment_processor: {
    processor_id: string;
    processor_key: string;
    payment_method_configuration_id: string | null;
    is_link_preference_enabled: boolean;
    is_brazilian_account_and_processor_stripe_br: boolean;
    is_tax_collection_enabled: boolean;
  };
  privacy_url: string;
  terms_url: string;
}

export interface CheckoutSessionAuthProvider {
  is_sso_authentication_required: boolean;
  name: string;
}

export interface CheckoutSession {
  auth_provider?: CheckoutSessionAuthProvider;
  checkout_intent_token?: string;
  community: CheckoutSessionCommunity;
  community_member?: CheckoutSessionCommunityMember;
  created_at: string;
  id: number;
  policies: {
    is_member_address_required: boolean;
    is_member_address_complete: boolean;
    should_show_tax_id_form: boolean;
  };
  settings: {
    login_url: string;
    reset_password_url: string;
    community_root_url: string;
    return_to?: string;
    checkout_confirmation_url: string;
    conversion_tracking_settings?: {
      meta_pixel_id?: string;
      meta_fbc?: string;
    } | null;
  };
  status: CheckoutSessionStatus;
  steps: CheckoutSessionStep[];
  total_amount_paid?: number | null;
  user?: Pick<
    CurrentUser,
    "id" | "name" | "first_name" | "last_name" | "email" | "avatar_url"
  >;
}
